<template>
  <div :class="$style.container">
    <ModalHeader
      :handleClose="handleClose"
      :headerIsClickable="true"
      :headerText="headerText"
      :showBackArrow="showUserLimitWarning"
      :showClose="true"
      @headerClicked="handleChoosePlan"
    />
    <div
      :class="$style.layoutContainer"
      data-test-id="PaymentForm_checkoutContainer"
    >
      <div
        :class="$style.main"
      >
        <HeadingMedium>
          Purchase {{ subscription.displayName }}
        </HeadingMedium>
        <CheckoutFlow
          v-if="showUserLimitWarning"
          :id="id"
          :class="$style.checkoutWrapper"
          :components="components"
          :location="location"
          :productCode="productCode"
          @cancelled="handleClose"
          @error="handleError"
          @success="handleSuccess"
        />
        <PaymentsWrapper
          v-else
          :location="location"
          :productCode="productCode"
          @cancelled="handleClose"
          @error="handleError"
          @submitted="handleSubmit"
          @success="handleSuccess"
        />
      </div>
      <CheckoutSummary
        v-if="showUserLimitWarning"
        :class="$style.aside"
        :subscription="subscription"
        v-on="$listeners"
      />
    </div>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  HeadingMedium,
} from '@jumpcloud/ui-components';
import {
  eventLocations,
  paymentModalNames,
  paymentSources,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import CheckoutFlow from 'client/Payments/newComponents/CheckoutFlow.vue';
import CheckoutSummary from 'client/Payments/newComponents/CheckoutSummary.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import LocalStorageConsts from 'client/util/Constants/LocalStorageConstants';
import LocalStorageService from 'client/util/LocalStorageService';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ModalHeader from 'components/Modal/ModalComponents/ModalHeader.vue';
import NudgeHelper from 'client/components/Nudge/NudgeHelper';
import PaymentFormMixin from 'client/Payments/mixins/PaymentFormMixin';
import PaymentStepMixin from 'client/Payments/mixins/PaymentStepMixin';
import PaymentsWrapper from 'client/Payments/PaymentsWrapper';
import PricingCalculatorModal from 'client/Pricing/PricingCalculator/PricingCalculatorModal.vue';

const {
  PaymentConsts,
} = Constants;
const { error } = Constants.default;
const {
  paymentMethods,
} = PaymentConsts;
const checkoutModel = 'CheckoutModel';
const pricingCalculatorModule = 'PricingCalculatorModule';
const pricingModule = 'PricingModule';
const storeModel = 'PaymentModel';
const settingsModule = 'SettingsModule';

const {
  paymentSetupModal,
  pricingCalculatorModal,
  pricingPage,
} = eventLocations;

const { paywallAbandoned } = NudgeHelper.availableNudges;

export default {
  name: 'PaymentForm',

  components: {
    CheckoutSummary,
    HeadingMedium,
    ModalHeader,
    CheckoutFlow,
    PaymentsWrapper,
  },

  mixins: [
    PaymentFormMixin,
    PaymentStepMixin,
  ],

  EventHelper,
  EventManager,
  LocalStorageService,
  ModalEventBus,
  NudgeHelper,

  props: {
    showUserLimitWarning: Boolean,
    source: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    loading: false,
    location: paymentSetupModal,
    modalName: paymentModalNames.paymentSetup, // to send with Segment events
    paymentMethodSelected: false,
  }),

  computed: {
    ...mapGetters(checkoutModel, {
      subscription: 'item',
    }),

    ...mapState(settingsModule, ['hasCreditCard']),

    components() {
      return this.subscription?.addOns?.map(addOn => addOn.productCode);
    },

    headerText() {
      return this.showUserLimitWarning ? 'Back to Pricing' : '';
    },

    productCode() {
      if (!this.subscription) return null;
      return this.subscription.productCode;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(storeModel, [
      'setIsProvider',
      'setProductCode',
      'setSubscription',
    ]),
    ...mapActions(settingsModule, ['fetchSettings']),
    ...mapActions(pricingCalculatorModule, ['resetAnswers']),
    ...mapActions(pricingModule, ['setServiceAgreementAccepted']),

    handleChoosePlan() {
      switch (this.source) {
        case pricingCalculatorModal:
          this.$options.ModalEventBus.open({
            component: PricingCalculatorModal,
            isLargeModal: true,
          });
          return;
        case pricingPage:
          this.$options.ModalEventBus.close();
          if (this.subscription.addOns.length > 0) {
            this.resetSubscription();
          }
          return;
        default:
          EventManager.triggerPaymentModalViewed({
            modal: paymentModalNames.paymentIntroModal,
            source: paymentSources.modalHeaderButton,
          });
          this.handleLastStep();
      }
    },

    handleClose() {
      if (this.source === pricingCalculatorModal) {
        this.resetAnswers();
      }

      this.resetSubscription();
      this.setServiceAgreementAccepted(false);
      this.$options.ModalEventBus.close();
      if (!this.isProvider) {
        EventManager.triggerPaymentModalDismissed();
      } else {
        this.$options.EventHelper.triggerProviderPaymentModalDismissed();
      }

      this.$options.NudgeHelper.setup(paywallAbandoned);
    },

    handleError(message) {
      this.loading = false;
      notify(error, message);
    },

    async handleMethodSelected(option) {
      if (option === paymentMethods.addNew) {
        this.paymentMethodSelected = true;
      } else if (option === paymentMethods.useCurrent) {
        this.loading = true;
        try {
          await this.setSubscription(this.id);
          this.handleSuccess();
          // Fetch settings so the admin can add new users if they were previously blocked.
          await this.fetchSettings();
        } catch (err) {
          this.handleError(err);
        }
      }
    },

    handleMounted() {
      if (this.subscription && this.subscription.productCode) {
        this.setProductCode({ productCode: this.subscription.productCode });
      }
      this.setIsProvider(this.isProvider);
    },

    handleSubmit() {
      // consumed by NewlySelfServedBannerConfig
      this.$options.LocalStorageService.setItem(
        LocalStorageConsts.customerSelfServedTimestamp,
        new Date(),
      );
    },

    handleSuccess() {
      this.loading = false;
      this.handleNextStep();
    },
  },
};
</script>

<style>
#CollectJSIframe {
  z-index: 5000 !important;
}
</style>

<style scoped module>
.container {
  display: flex;
  flex-direction: column;
  height: 95vh;
}

.checkoutWrapper {
  overflow-y: auto;
  padding-right: 30px;
}

.layoutContainer {
  display: flex;
  justify-content: center;
  overflow-y: auto;
  padding: 30px 100px;
  position: relative;
}

.main {
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 800px;
  padding: 20px 0 20px 30px;
}

.aside {
  width: 550px;
}

@media (max-width: 1425px) {
  .aside {
    width: 450px;
  }
}

</style>
